import workspaceSideNavCSS from './workspace-side-nav.css?inline';
/**
 * M5T Side Navigation Web Component  —  <m5t-side-nav>
 *
 * Persona-aware LEFT navigation for client-saas, faithful to the MachFiveMagnet
 * demo sidebar (dark sidebar + light content). Sibling to m5t-primary-nav,
 * reusing the same conventions (Shadow DOM, CustomEvent + pubsubTopic).
 *
 * Intentionally "dumb": it renders whatever capability-filtered, i18n-resolved
 * state it is GIVEN (nav tree, orgs, active org, persona badge), emits events, and
 * exposes setActiveToken()/collapse — it does NOT know about tenancy or i18n. The
 * app (main.js) feeds it from AuthController scope and re-feeds on AUTH.SCOPE.LOADED.
 *
 * State (set via setState({buttons, orgs, activeOrg, footerBadge}) — one render):
 * - buttons     nav tree (items and/or {group,items} nodes) — capability-filtered upstream
 * - orgs        [{guid, name, channel, group?}] for the footer org switcher (shown when >1).
 *               Entries sharing a `group` label render inside an <optgroup> (e.g. a
 *               reseller's "Your organization" vs "Clients"); group-less entries render flat.
 * - activeOrg   active org guid (selected in the switcher)
 * - footerBadge {caption, icon} persona badge
 * Attributes: data-collapsed ("true"/"false", persisted), data-active-token (route-driven),
 *             hidden (host hides itself; app shows on authed routes), data-open (mobile drawer).
 *
 * Events (bubbling + composed):
 * - nav-select    { pubsubTopic:'M5T_SIDE_NAV.TAP', token, route, caption }
 * - nav-collapse  { pubsubTopic:'M5T_SIDE_NAV.COLLAPSE', collapsed }
 * - logo-click    { pubsubTopic:'M5T_SIDE_NAV.LOGO_TAP' }
 * - org-switch    { pubsubTopic:'M5T_SIDE_NAV.ORG_SWITCH', orgGuid }
 * - footer-action { pubsubTopic:'M5T_SIDE_NAV.FOOTER_ACTION', action:'account'|'lang'|'theme'|'signout' }
 *
 * Public methods: setState(partial); setActiveToken(token) (surgical, no re-render);
 * setButtons/setOrgs/setActiveOrgGuid/setFooterBadge (setState wrappers); toggleCollapsed(force?).
 */

const COLLAPSE_KEY = 'm5t_sidenav_collapsed';

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

class M5TSideNav extends HTMLElement {
  static get observedAttributes() {
    return ['data-active-token', 'data-collapsed'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.items = [];
    this.orgs = [];
    this.activeOrgGuid = null;
    this.activeToken = null;
    this.activeChildToken = null;
    this.expandedSections = new Set();
    this.footerBadge = null;
    this.loading = false;
    this.lastFocus = null;
    this.flyoutToken = null;
  }

  // Mobile-drawer focus-trap + Escape; bound once, attached in connectedCallback.
  #mql = null;
  #keydown = (e) => {
    if (this.flyoutToken && e.key === 'Escape') { e.preventDefault(); this.#closeFlyout(true); return; }
    if (this.getAttribute('data-open') !== 'true') return;
    if (e.key === 'Escape') { e.preventDefault(); this.closeDrawer(); return; }
    if (e.key === 'Tab') {
      const f = this.#focusables();
      if (f.length < 2) return;
      const first = f[0], last = f[f.length - 1];
      const active = this.shadowRoot.activeElement;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }
  };
  #outsidePointer = e => { if (!e.composedPath().includes(this)) this.#closeFlyout(); };
  #outsideFocus = e => {
    if (!this.flyoutToken) return;
    const active = this.shadowRoot.activeElement;
    if (!active?.closest('.nav-flyout, .section-trigger')) this.#closeFlyout();
  };
  #onResize = () => this.#closeFlyout();
  #onMediaChange = () => { this.#closeFlyout(); this.closeDrawer(); this.render(); this.#attach(); };

  connectedCallback() {
    // Restore collapsed state (attribute wins, else localStorage)
    if (!this.hasAttribute('data-collapsed')) {
      try {
        if (localStorage.getItem(COLLAPSE_KEY) === 'true') this.setAttribute('data-collapsed', 'true');
      } catch { /* ignore */ }
    }
    this.activeToken = this.getAttribute('data-active-token') || null;
    this.render();
    this.#attach();
    this.addEventListener('keydown', this.#keydown);
    document.addEventListener('pointerdown', this.#outsidePointer);
    document.addEventListener('focusin', this.#outsideFocus);
    window.addEventListener('resize', this.#onResize);
    this.#mql = window.matchMedia('(max-width: 768px)');
    this.#mql.addEventListener('change', this.#onMediaChange);
    this.#syncInert();
  }

  disconnectedCallback() {
    this.closeDrawer();
    this.removeEventListener('keydown', this.#keydown);
    document.removeEventListener('pointerdown', this.#outsidePointer);
    document.removeEventListener('focusin', this.#outsideFocus);
    window.removeEventListener('resize', this.#onResize);
    this.#mql?.removeEventListener('change', this.#onMediaChange);
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal === newVal || !this.shadowRoot) return;
    if (name === 'data-active-token') {
      this.activeToken = newVal;
      this.#applyActive();           // surgical — no re-render
    } else if (name === 'data-collapsed') {
      if (this.shadowRoot.firstChild) { this.render(); this.#attach(); } // width/labels/chevron change
    }
  }

  // ---- public API ----
  /** Batched state update — single render. */
  setState({ buttons, orgs, activeOrg, footerBadge, loading } = {}) {
    if (buttons !== undefined) this.items = Array.isArray(buttons) ? buttons : (this.#safeParse(buttons) || []);
    if (orgs !== undefined) this.orgs = Array.isArray(orgs) ? orgs : (this.#safeParse(orgs) || []);
    if (activeOrg !== undefined) this.activeOrgGuid = activeOrg || null;
    if (footerBadge !== undefined) this.footerBadge = footerBadge || null;
    if (loading !== undefined) this.loading = !!loading;
    if (this.shadowRoot) { this.render(); this.#attach(); }
  }

  setButtons(data) { this.setState({ buttons: data }); }
  setOrgs(orgs) { this.setState({ orgs }); }
  setActiveOrgGuid(guid) { this.setState({ activeOrg: guid }); }
  setFooterBadge(badge) { this.setState({ footerBadge: badge }); }

  setActiveToken(token, childToken = null) {
    this.#closeFlyout();
    this.activeToken = token || null;
    this.activeChildToken = childToken;
    if (token) this.expandedSections.add(token);
    this.#applyActive();
    this.#syncSections();
  }

  toggleCollapsed(force) {
    const next = typeof force === 'boolean' ? force : this.getAttribute('data-collapsed') !== 'true';
    this.setAttribute('data-collapsed', String(next));
    try { localStorage.setItem(COLLAPSE_KEY, String(next)); } catch { /* ignore */ }
    this.dispatchEvent(new CustomEvent('nav-collapse', {
      bubbles: true, composed: true,
      detail: { pubsubTopic: 'M5T_SIDE_NAV.COLLAPSE', collapsed: next, timestamp: Date.now() },
    }));
  }

  /** Open the mobile off-canvas drawer (focuses into it; focus is trapped until closed). */
  openDrawer() {
    this.lastFocus = this.shadowRoot.activeElement || document.activeElement;
    this.setAttribute('data-open', 'true');
    this.shadowRoot.querySelector('.hamburger')?.setAttribute('aria-expanded', 'true');
    this.#syncInert();
    const f = this.#focusables();
    (f[0] || this.shadowRoot.querySelector('.sidenav'))?.focus?.();
  }

  /** Close the mobile drawer and return focus to the trigger. No-op on desktop. */
  closeDrawer() {
    if (this.getAttribute('data-open') !== 'true') return;
    this.removeAttribute('data-open');
    const ham = this.shadowRoot.querySelector('.hamburger');
    ham?.setAttribute('aria-expanded', 'false');
    this.#syncInert();
    if (ham && this.#mql?.matches) ham.focus();
    else this.lastFocus?.focus?.();
  }

  toggleDrawer() {
    if (this.getAttribute('data-open') === 'true') this.closeDrawer();
    else this.openDrawer();
  }

  // ---- internals ----
  #safeParse(raw) {
    try { return raw ? JSON.parse(raw) : null; }
    catch (e) { console.error('[M5TSideNav] bad JSON:', e); return null; }
  }

  #esc(s) { return String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]); }

  #icon(name) {
    return name ? `<span class="ms" aria-hidden="true">${this.#esc(name)}</span>` : '';
  }

  #itemHTML(item) {
    if (!item || !item.token) return '';
    const active = item.token === this.activeToken || item.token === this.activeChildToken;
    const current = item.token === (this.activeChildToken || this.activeToken);
    const route = item.route || '#';
    const href = route.startsWith('#') ? route : `#${route}`;
    const count = (typeof item.count === 'number' && item.count > 0)
      ? `<span class="badge">${item.count}</span>` : '';
    return `
      <a class="nav-item${item.childLink ? ' nav-subitem' : ''}${active ? ' active' : ''}" href="${this.#esc(href)}"
         data-token="${this.#esc(item.token)}" data-route="${this.#esc(route)}" aria-label="${this.#esc(item.caption || item.token)}"
         ${current ? 'aria-current="page"' : ''}
         ${item.tagline ? `title="${this.#esc(item.tagline)}"` : ''}>
        ${this.#icon(item.icon)}
        <span class="label">${this.#esc(item.caption || item.token)}</span>
        ${count}
      </a>`;
  }

  #nodeHTML(item) {
    if (!item?.showChildren || !item.children?.length) return this.#itemHTML(item);
    if (this.#compactDesktop()) {
      return `<button type="button" class="nav-item section-trigger${item.token === this.activeToken ? ' active' : ''}"
        data-section-toggle="${this.#esc(item.token)}" aria-label="${this.#esc(item.caption)} links"
        title="${this.#esc(item.caption)}" aria-expanded="false" aria-controls="nav-flyout">
        ${this.#icon(item.icon)}<span class="section-caret" aria-hidden="true">›</span></button>`;
    }
    const open = this.expandedSections.has(item.token);
    const id = `nav-children-${item.token}`;
    return `<div class="nav-section">
      <div class="nav-section-head">${this.#itemHTML(item)}
        <button type="button" class="section-toggle" data-section-toggle="${this.#esc(item.token)}"
          aria-label="${this.#esc(item.caption)} links" aria-expanded="${open}" aria-controls="${this.#esc(id)}">
          ${this.#icon('expand_more')}
        </button>
      </div>
      <div id="${this.#esc(id)}" class="nav-children" ${open ? '' : 'hidden'}>
        ${item.children.map(child => this.#itemHTML({ ...child, childLink: true })).join('')}
      </div>
    </div>`;
  }

  #syncSections() {
    this.shadowRoot.querySelectorAll('[data-section-toggle]').forEach(button => {
      const open = this.#compactDesktop() ? this.flyoutToken === button.dataset.sectionToggle : this.expandedSections.has(button.dataset.sectionToggle);
      button.setAttribute('aria-expanded', String(open));
      this.shadowRoot.getElementById(button.getAttribute('aria-controls')).hidden = !open;
    });
  }

  #compactDesktop() {
    return this.getAttribute('data-collapsed') === 'true' && !window.matchMedia('(max-width: 768px)').matches;
  }

  #closeFlyout(restoreFocus = false) {
    if (!this.flyoutToken) return;
    const trigger = [...this.shadowRoot.querySelectorAll('[data-section-toggle]')].find(b => b.dataset.sectionToggle === this.flyoutToken);
    this.flyoutToken = null;
    const panel = this.shadowRoot.querySelector('.nav-flyout');
    if (panel) { panel.hidden = true; panel.innerHTML = ''; }
    trigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) trigger?.focus();
  }

  #openFlyout(button) {
    const token = button.dataset.sectionToggle;
    if (this.flyoutToken === token) { this.#closeFlyout(); return; }
    const item = this.items.flatMap(node => node.items || [node]).find(node => node.token === token);
    if (!item?.children?.length) return;
    this.#closeFlyout();
    this.flyoutToken = token;
    const panel = this.shadowRoot.querySelector('.nav-flyout');
    panel.setAttribute('aria-label', item.caption);
    panel.innerHTML = `<div class="flyout-title">${this.#esc(item.caption)}</div>${item.children.map(child => this.#itemHTML({ ...child, childLink: true })).join('')}`;
    panel.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    const anchor = button.getBoundingClientRect();
    panel.style.left = `${this.shadowRoot.querySelector('.sidenav').getBoundingClientRect().right + 8}px`;
    panel.style.top = `${Math.max(8, Math.min(anchor.top, window.innerHeight - panel.offsetHeight - 8))}px`;
    this.#attachLinks(panel);
    panel.querySelector('a')?.focus();
  }

  #treeHTML() {
    if (this.loading) {
      return Array.from({ length: 6 }).map(() =>
        `<div class="nav-skel" aria-hidden="true"><span class="skel-dot"></span><span class="skel-bar"></span></div>`).join('');
    }
    return this.items.map((node) => {
      if (node && node.group && Array.isArray(node.items)) {
        return `
          <div class="nav-group">
            <div class="group-label">${this.#esc(node.caption || '')}</div>
            ${node.items.map((i) => this.#nodeHTML(i)).join('')}
          </div>`;
      }
      return this.#nodeHTML(node);
    }).join('');
  }

  #footerHTML() {
    const badge = this.footerBadge;
    // Preserve insertion order; consecutive same-`group` entries share an optgroup.
    const opt = (o) => `<option value="${this.#esc(o.guid)}" ${o.guid === this.activeOrgGuid ? 'selected' : ''}>${this.#esc(o.name || o.guid)}</option>`;
    const grouped = [];
    for (const o of (Array.isArray(this.orgs) ? this.orgs : [])) {
      const label = o.group || '';
      const last = grouped[grouped.length - 1];
      if (last && last.label === label) last.items.push(o);
      else grouped.push({ label, items: [o] });
    }
    const options = grouped.map((g) => g.label
      ? `<optgroup label="${this.#esc(g.label)}">${g.items.map(opt).join('')}</optgroup>`
      : g.items.map(opt).join('')
    ).join('');
    const switcher = (Array.isArray(this.orgs) && this.orgs.length > 1) ? `
      <label class="org-switcher" title="Active organization">
        ${this.#icon('corporate_fare')}
        <select class="org-select" aria-label="Active organization">
          ${options}
        </select>
      </label>` : '';
    const billingLine = badge?.billingProvider === 'shopify'
      ? '<span class="footer-badge-billing"><img src="integrations/shopify.svg" alt="" width="13" height="15">Billed through Shopify</span>' : '';
    const badgeHTML = badge
      ? `<span class="footer-badge${billingLine ? ' has-billing' : ''}" title="${this.#esc(badge.caption || '')}${billingLine ? ' · Billed through Shopify' : ''}">${this.#icon(badge.icon || 'verified')}<span class="footer-badge-copy"><span>${this.#esc(badge.caption || '')}</span>${billingLine}</span></span>`
      : '';
    return `
      ${switcher}
      ${badgeHTML}
      <div class="footer-actions">
        <button class="footer-action" type="button" data-action="account" aria-label="Account" title="Account">${this.#icon('account_circle')}<span>Account</span></button>
        <!-- Language button removed 2026-09-14 (Nick's call): hidden for all
             users, functionality kept - main.js still handles the 'lang'
             action and the whole I18nController path stays live, so restoring
             the surface is reinserting this one button. -->
        <button class="footer-action" type="button" data-action="theme" aria-label="Toggle dark mode" title="Toggle dark mode">${this.#icon('dark_mode')}<span>Theme</span></button>
        <button class="footer-action" type="button" data-action="signout" aria-label="Sign out" title="Sign out">${this.#icon('logout')}<span>Sign out</span></button>
      </div>`;
  }

  render() {
    this.flyoutToken = null;
    const collapsed = this.getAttribute('data-collapsed') === 'true';
    this.shadowRoot.innerHTML = `
      <style>
        :host { --sidenav-width: 260px; --sidenav-width-collapsed: 68px; display: block; }
        :host([hidden]) { display: none; }

        .sidenav {
          position: fixed; inset: 0 auto 0 0;
          width: var(--sidenav-width);
          background: var(--color-sidebar, #0F2435);
          color: var(--color-sidebar-text, rgba(242,247,251,0.72));
          display: flex; flex-direction: column;
          box-shadow: 2px 0 10px rgba(0,0,0,0.18);
          z-index: 90;
          font-family: var(--font-body, 'Inter', system-ui, sans-serif);
          transition: width 0.2s ease, transform 0.2s ease;
          overflow: hidden;
        }
        :host([data-collapsed="true"]) .sidenav { width: var(--sidenav-width-collapsed); }

        /* Material Symbols (rule must live in the shadow root; the @font-face from
           the document still applies across the boundary). */
        .ms {
          font-family: 'Material Symbols Outlined';
          font-weight: normal; font-style: normal; font-size: 22px; line-height: 1;
          letter-spacing: normal; text-transform: none; display: inline-block;
          white-space: nowrap; direction: ltr; flex: 0 0 auto;
          -webkit-font-feature-settings: 'liga'; -webkit-font-smoothing: antialiased;
          -webkit-user-select: none; user-select: none;
        }

        .header {
          display: flex; align-items: center; gap: 12px;
          padding: 18px 16px 18px 20px;
          border-bottom: 1px solid var(--color-sidebar-border, rgba(255,255,255,0.08));
        }
        .logo { display: flex; align-items: center; gap: 10px; cursor: pointer; min-width: 0; background: none; border: 0; padding: 0; color: inherit; }
        .logo img { width: 28px; height: 28px; display: block; flex: 0 0 auto; }
        .logo .wordmark {
          font-family: var(--font-display, 'Inter', system-ui, sans-serif);
          font-size: 1.05rem; font-weight: 600; letter-spacing: -0.01em;
          color: var(--color-sidebar-text-active, #F2F7FB); white-space: nowrap;
        }
        .logo .wordmark b { color: var(--color-sidebar-accent, #22A0FF); font-weight: 700; }
        .collapse-btn {
          margin-left: auto; appearance: none; background: transparent; border: 0; cursor: pointer;
          color: var(--color-sidebar-text, rgba(242,247,251,0.72));
          display: grid; place-items: center; width: 32px; height: 32px; border-radius: 8px;
          transition: background 0.15s ease, color 0.15s ease; flex: 0 0 auto;
        }
        .collapse-btn:hover { background: var(--color-sidebar-2, #17334A); color: var(--color-sidebar-text-active, #F2F7FB); }

        .nav { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 16px 0; }
        .nav-group { padding: 6px 0; }
        .group-label {
          padding: 8px 20px 4px; font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.08em; color: var(--color-sidebar-text, rgba(242,247,251,0.5)); opacity: 0.7;
        }

        .nav-section-head { display: flex; align-items: stretch; }
        .nav-section-head > .nav-item { flex: 1; min-width: 0; }
        .section-toggle { width: 36px; flex: 0 0 36px; border: 0; background: transparent; color: inherit; cursor: pointer; }
        .section-toggle:hover { background: rgba(255,255,255,0.08); }
        .section-toggle:focus-visible { outline: 2px solid var(--color-sidebar-text-active, #F2F7FB); outline-offset: -3px; }
        .section-toggle[aria-expanded="true"] .ms { transform: rotate(180deg); }
        .nav-children[hidden] { display: none; }
        .nav-item.nav-subitem { padding: 9px 16px 9px 52px; font-size: 0.8rem; }
        :host([data-collapsed="true"]) .nav-children { display: none; }
        .section-trigger { width: 100%; border: 0; border-left: 3px solid transparent; background: transparent; cursor: pointer; position: relative; font: inherit; }
        .section-caret { position: absolute; right: 7px; font-size: 18px; }
        .section-trigger[aria-expanded="true"] { background: var(--color-sidebar-2, #17334A); color: var(--color-sidebar-text-active, #F2F7FB); }
        .section-trigger:focus-visible, .nav-item:focus-visible { outline: 2px solid var(--color-sidebar-accent, #22A0FF); outline-offset: -3px; }
        .nav-flyout { position: fixed; z-index: 1101; width: 260px; max-width: calc(100vw - 92px); max-height: calc(100dvh - 16px); overflow-y: auto;
          box-sizing: border-box; padding: 8px; border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-lg, 12px);
          background: var(--color-bg, #fff); color: var(--color-text, #0B1220); box-shadow: var(--shadow-lg, 0 12px 32px rgba(11,18,32,.2));
          font-family: var(--font-body, 'Inter', system-ui, sans-serif); }
        .nav-flyout[hidden] { display: none; }
        .flyout-title { padding: 8px 12px 10px; font-size: .8rem; font-weight: 700; color: var(--color-text-muted, #4A5A78); }
        :host([data-collapsed="true"]) .nav-flyout .nav-item { padding: 12px; gap: 10px; justify-content: flex-start; white-space: normal; color: var(--color-text, #0B1220); border-radius: 6px; font-size: .9rem; }
        :host([data-collapsed="true"]) .nav-flyout .nav-item .label { display: block; }
        .nav-flyout .nav-item:hover { background: var(--color-surface, #F4F7FB); }
        .nav-flyout .nav-item.active { background: color-mix(in srgb, var(--color-primary-600, #0072BA) 12%, var(--color-bg, #fff)); color: var(--color-primary-600, #0072BA); }

        /* Loading skeleton (shown while tenancy scope resolves on a cold sign-in) */
        .nav-skel { display: flex; align-items: center; gap: 12px; padding: 13px 20px; }
        .skel-dot { width: 22px; height: 22px; border-radius: 6px; background: rgba(255,255,255,0.08); flex: 0 0 auto; animation: m5t-skel 1.2s ease-in-out infinite; }
        .skel-bar { height: 10px; border-radius: 5px; background: rgba(255,255,255,0.08); flex: 1 1 auto; max-width: 58%; animation: m5t-skel 1.2s ease-in-out infinite; }
        @keyframes m5t-skel { 0%, 100% { opacity: 0.45; } 50% { opacity: 0.9; } }

        .nav-item {
          display: flex; align-items: center; gap: 12px;
          padding: 13px 20px;
          color: var(--color-sidebar-text, rgba(242,247,251,0.72));
          text-decoration: none; font-weight: 500; font-size: 0.95rem;
          border-left: 3px solid transparent; transition: background 0.15s ease, color 0.15s ease;
          white-space: nowrap;
        }
        .nav-item:hover { background: rgba(255,255,255,0.05); color: var(--color-sidebar-text-active, #F2F7FB); }
        .nav-item.active {
          background: color-mix(in srgb, var(--color-sidebar-accent, #22A0FF) 16%, transparent);
          color: var(--color-sidebar-text-active, #F2F7FB);
          border-left-color: var(--color-sidebar-accent, #22A0FF);
        }
        .nav-item.active .ms { color: var(--color-sidebar-accent, #22A0FF); }
        .nav-item .label { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; }
        .badge {
          flex: 0 0 auto; min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px;
          background: var(--color-sidebar-accent, #22A0FF); color: #06243a;
          font-size: 0.7rem; font-weight: 700; display: grid; place-items: center;
        }

        .footer {
          padding: 14px 16px; border-top: 1px solid var(--color-sidebar-border, rgba(255,255,255,0.08));
          display: flex; flex-direction: column; gap: 12px;
        }
        .org-switcher {
          display: flex; align-items: center; gap: 8px;
          background: var(--color-sidebar-2, #17334A); border-radius: 8px; padding: 6px 10px;
        }
        .org-switcher .ms { font-size: 18px; color: var(--color-sidebar-accent, #22A0FF); }
        .org-select {
          flex: 1 1 auto; min-width: 0; background: transparent; border: 0;
          color: var(--color-sidebar-text-active, #F2F7FB); font: inherit; font-size: 0.82rem;
          cursor: pointer; outline: none;
          appearance: none; -webkit-appearance: none;
          background-image: var(--m5t-select-chevron-inverse);
          background-repeat: no-repeat; background-position: right 4px center;
          background-size: 16px 16px; padding: 4px 28px 4px 0;
          text-overflow: ellipsis; border-radius: 4px;
        }
        .org-select:focus-visible { outline: 2px solid var(--color-sidebar-accent, #22A0FF); outline-offset: 2px; }
        .org-select:disabled { opacity: .55; cursor: not-allowed; }
        .org-select:dir(rtl) { background-position: left 4px center; padding: 4px 0 4px 28px; }
        @media (forced-colors: active) {
          .org-select { appearance: auto; background-image: none; }
        }
        .org-select option, .org-select optgroup { color: #0B1220; } /* native dropdown list paints light */
        .footer-badge {
          display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 8px;
          background: color-mix(in srgb, var(--color-sidebar-accent, #22A0FF) 14%, transparent);
          border: 1px solid color-mix(in srgb, var(--color-sidebar-accent, #22A0FF) 30%, transparent);
          color: var(--color-sidebar-text-active, #F2F7FB); font-size: 0.78rem; font-weight: 600;
          align-self: flex-start;
        }
        .footer-badge .ms { font-size: 18px; flex: none; }
        .footer-badge.has-billing { align-items: flex-start; }
        .footer-badge-copy { display: grid; gap: 4px; }
        .footer-badge-billing { display: flex; align-items: center; gap: 5px; font-size: 10px; font-weight: 500; white-space: nowrap; }
        .footer-badge-billing img { display: block; flex: none; object-fit: contain; }
        .footer-actions { display: flex; gap: 4px; }
        .footer-action {
          flex: 1 1 0; display: flex; flex-direction: column; align-items: center; gap: 4px;
          background: transparent; border: 0; cursor: pointer; padding: 8px 4px; border-radius: 8px;
          color: var(--color-sidebar-text, rgba(242,247,251,0.72)); font: inherit; font-size: 0.66rem; font-weight: 500;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .footer-action:hover { background: rgba(255,255,255,0.06); color: var(--color-sidebar-text-active, #F2F7FB); }
        .footer-action .ms { font-size: 20px; }

        /* Collapsed (icon-only) */
        :host([data-collapsed="true"]) .logo .wordmark,
        :host([data-collapsed="true"]) .group-label,
        :host([data-collapsed="true"]) .nav-item .label,
        :host([data-collapsed="true"]) .nav-item .badge,
        :host([data-collapsed="true"]) .org-switcher,
        :host([data-collapsed="true"]) .skel-bar,
        :host([data-collapsed="true"]) .footer-action span:not(.ms),
        :host([data-collapsed="true"]) .footer-badge span:not(.ms) { display: none; }
        :host([data-collapsed="true"]) .nav-item { justify-content: center; gap: 0; padding-left: 0; padding-right: 0; }
        /* Header stacks: logo mark on top, expand chevron centered below it. */
        :host([data-collapsed="true"]) .header { flex-direction: column; justify-content: center; gap: 10px; padding: 16px 0; }
        :host([data-collapsed="true"]) .logo { justify-content: center; gap: 0; }
        :host([data-collapsed="true"]) .collapse-btn { margin-left: 0; }
        /* Footer becomes a centered icon stack: persona badge, then account/lang/sign-out
           (sign-out anchored at the bottom). Reset the row-layout flex so icons don't stretch. */
        :host([data-collapsed="true"]) .footer { padding: 12px 0 16px; align-items: center; gap: 10px; }
        :host([data-collapsed="true"]) .footer-badge { padding: 8px; align-self: center; }
        :host([data-collapsed="true"]) .footer-actions { flex-direction: column; gap: 4px; width: auto; }
        :host([data-collapsed="true"]) .footer-action { flex: 0 0 auto; justify-content: center; width: 40px; height: 40px; padding: 0; border-radius: 8px; }

        /* Hamburger + scrim: desktop hidden; shown on mobile (off-canvas drawer) */
        .hamburger, .scrim { display: none; }

        @media (max-width: 768px) {
          .sidenav { transform: translateX(-100%); width: var(--sidenav-width); box-shadow: 4px 0 24px rgba(0,0,0,0.4); }
          :host([data-open="true"]) .sidenav { transform: translateX(0); }
          :host([data-collapsed="true"]) .sidenav { width: var(--sidenav-width); }
          .collapse-btn { display: none; } /* collapse is a desktop-only affordance */
          /* A saved desktop collapse preference must not turn the mobile drawer
             into unlabeled icons or hide its direct report links. */
          :host([data-collapsed="true"]) .nav-item .label,
          :host([data-collapsed="true"]) .group-label { display: block; }
          :host([data-collapsed="true"]) .org-switcher { display: flex; }
          :host([data-collapsed="true"]) .nav-item { justify-content: flex-start; gap: 12px; padding-left: 20px; padding-right: 20px; }
          :host([data-collapsed="true"]) .nav-item.nav-subitem { padding-left: 52px; }
          :host([data-collapsed="true"]) .nav-children:not([hidden]) { display: block; }
          :host([data-collapsed="true"]) .section-toggle { width: 36px; flex-basis: 36px; }

          .hamburger {
            display: grid; place-items: center;
            position: fixed; top: 12px; left: 12px; z-index: 1100;
            width: 44px; height: 44px; border-radius: 10px; border: 0; cursor: pointer;
            background: var(--color-sidebar, #0F2435);
            color: var(--color-sidebar-text-active, #F2F7FB);
            box-shadow: var(--shadow-md, 0 8px 24px rgba(11,18,32,0.18));
          }
          .hamburger .ms { font-size: 24px; }
          :host([data-open="true"]) .hamburger { display: none; }

          .scrim { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 88; }
          :host([data-open="true"]) .scrim { display: block; }
        }

        @media (prefers-reduced-motion: reduce) {
          .sidenav { transition: none; }
        }
        ${workspaceSideNavCSS}
      </style>

      <button class="hamburger" type="button" aria-label="Open navigation menu"
        aria-expanded="${this.getAttribute('data-open') === 'true' ? 'true' : 'false'}" aria-controls="m5t-sidenav-aside">
        <span class="ms" aria-hidden="true">menu</span>
      </button>
      <div class="scrim" part="scrim"></div>

      <aside class="sidenav" id="m5t-sidenav-aside" part="sidenav">
        <div class="header">
          <button class="logo" type="button" aria-label="Mach Five Magnet home">
            <img src="m5m-mark.svg" alt="" width="28" height="28" />
            <span class="wordmark">Mach Five <b>Magnet</b></span>
          </button>
          <button class="collapse-btn" type="button" aria-label="Toggle sidebar"
            aria-expanded="${collapsed ? 'false' : 'true'}">
            <span class="ms" aria-hidden="true">${collapsed ? 'chevron_right' : 'chevron_left'}</span>
          </button>
          <button class="drawer-close" type="button" aria-label="Close navigation menu"><span class="ms" aria-hidden="true">close</span></button>
        </div>

        <nav class="nav" aria-label="Primary"${this.loading ? ' aria-busy="true"' : ''}>
          ${this.#treeHTML()}
        </nav>

        <div class="footer" part="footer">
          ${this.#footerHTML()}
        </div>
      </aside>
      <nav class="nav-flyout" id="nav-flyout" aria-label="Reports" hidden></nav>
    `;
  }

  #attach() {
    this.#attachLinks(this.shadowRoot);
    this.shadowRoot.querySelectorAll('[data-section-toggle]').forEach(button => {
      button.addEventListener('click', () => {
        if (this.#compactDesktop()) { this.#openFlyout(button); return; }
        const token = button.dataset.sectionToggle;
        if (!this.expandedSections.has(token)) this.expandedSections.add(token);
        else this.expandedSections.delete(token);
        this.#syncSections();
      });
    });

    this.shadowRoot.querySelector('.collapse-btn')?.addEventListener('click', () => this.toggleCollapsed());
    this.shadowRoot.querySelector('.hamburger')?.addEventListener('click', () => this.toggleDrawer());
    this.shadowRoot.querySelector('.scrim')?.addEventListener('click', () => this.closeDrawer());
    this.shadowRoot.querySelector('.drawer-close')?.addEventListener('click', () => this.closeDrawer());
    this.#syncInert();

    this.shadowRoot.querySelector('.logo')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('logo-click', {
        bubbles: true, composed: true,
        detail: { pubsubTopic: 'M5T_SIDE_NAV.LOGO_TAP', timestamp: Date.now() },
      }));
    });

    // Footer: active-org switcher
    this.shadowRoot.querySelector('.org-select')?.addEventListener('change', (e) => {
      this.dispatchEvent(new CustomEvent('org-switch', {
        bubbles: true, composed: true,
        detail: { pubsubTopic: 'M5T_SIDE_NAV.ORG_SWITCH', orgGuid: e.target.value, timestamp: Date.now() },
      }));
    });

    // Footer: account / language / sign out (closes the drawer, like nav items, so
    // data-open never leaks across a route-hide e.g. sign out → cancel → back).
    this.shadowRoot.querySelectorAll('.footer-action').forEach((b) => {
      b.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('footer-action', {
          bubbles: true, composed: true,
          detail: { pubsubTopic: 'M5T_SIDE_NAV.FOOTER_ACTION', action: b.getAttribute('data-action'), timestamp: Date.now() },
        }));
        this.closeDrawer();
      });
    });

    // If this render happened while the mobile drawer was open (e.g. an org switch or
    // language change re-feeds setState), the focused node was just destroyed by the
    // innerHTML replacement — pull focus back inside so the focus-trap keeps working.
    if (this.getAttribute('data-open') === 'true') {
      this.#focusables()[0]?.focus();
    }
  }

  #attachLinks(root) {
    root.querySelectorAll('a.nav-item').forEach(a => {
      a.addEventListener('click', e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        this.dispatchEvent(new CustomEvent('nav-select', {
          bubbles: true, composed: true,
          detail: { pubsubTopic: 'M5T_SIDE_NAV.TAP', token: a.getAttribute('data-token'),
            route: a.getAttribute('data-route'), caption: a.querySelector('.label')?.textContent || '', timestamp: Date.now() },
        }));
        this.#closeFlyout();
        this.closeDrawer();
      });
    });
  }

  /** Surgical active-state swap — no innerHTML re-render (cheap + listener-stable). */
  #applyActive() {
    if (!this.shadowRoot) return;
    this.shadowRoot.querySelectorAll('.nav-item').forEach((a) => {
      const token = a.getAttribute('data-token') || a.dataset.sectionToggle;
      const on = token === this.activeToken || token === this.activeChildToken;
      a.classList.toggle('active', on);
      if (a.tagName === 'A' && token === (this.activeChildToken || this.activeToken)) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    });
  }

  /** Focusable elements inside the drawer (for the mobile focus-trap). */
  #focusables() {
    return Array.from(
      this.shadowRoot.querySelectorAll('.sidenav a[href], .sidenav button:not([disabled]), .sidenav select')
    ).filter((el) => el.offsetParent !== null);
  }

  /** Off-canvas drawer must not be focusable/announced when closed on mobile. */
  #syncInert() {
    const aside = this.shadowRoot?.querySelector('.sidenav');
    if (!aside) return;
    const mobile = this.#mql ? this.#mql.matches : window.matchMedia('(max-width: 768px)').matches;
    aside.inert = mobile && this.getAttribute('data-open') !== 'true';
    if (this.getAttribute('data-shell') === 'workspace') {
      const open = mobile && this.getAttribute('data-open') === 'true';
      for (const id of ['workspace-header', 'app-content']) {
        const el = document.getElementById(id);
        if (el) el.inert = open;
      }
    }
  }
}

customElements.define('m5t-side-nav', M5TSideNav);

export default M5TSideNav;
